import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import './navbar.css'
import { useContact } from '../../context/ContactContext'
import logo from '../../assets/logo.png'
import { FiChevronDown, FiArrowUpRight } from 'react-icons/fi'

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'sq', label: 'SQ' },
  { code: 'mk', label: 'MK' },
]

const navLinks = [
  { href: '#about', key: 'about' },
  { href: '#process', key: 'process' },
  { href: '#services', key: 'services' },
  { href: '#technologies', key: 'technologies' },
  { href: '#portfolio', key: 'portfolio' },
  { href: '#contact', key: 'contact' },
]

const LangDropdown = ({ mobile = false }) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0]

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const select = (code) => {
    i18n.changeLanguage(code)
    setOpen(false)
  }

  return (
    <div className={`navbar__lang ${mobile ? 'navbar__lang--mobile' : ''}`} ref={ref}>
      <button
        type="button"
        className={`navbar__lang-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {currentLang.label}
        <FiChevronDown />
      </button>
      {open && (
        <ul className="navbar__lang-menu">
          {languages.map(({ code, label }) => (
            <li key={code}>
              <button
                type="button"
                className={i18n.language === code ? 'active' : ''}
                onClick={() => select(code)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const Navbar = () => {
  const { t } = useTranslation()
  const { openContact } = useContact()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const closeMenu = () => setMenuOpen(false)

  return (
    <>
      <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${menuOpen ? 'navbar--menu-open' : ''}`}>
        <div className="navbar__line" aria-hidden="true" />
        <div className="container navbar__inner">
          <a href="#home" className="navbar__brand" onClick={closeMenu}>
            <img src={logo} alt="UNICodes" className="navbar__brand-mark" />
          </a>

          <nav className="navbar__nav" aria-label="Main">
            <ul className="navbar__links">
              {navLinks.map(({ href, key }) => (
                <li key={key}>
                  <a href={href}>{t(`nav.${key}`)}</a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="navbar__actions">
            <LangDropdown />
            <button type="button" className="navbar__cta" onClick={openContact}>
              <span>{t('nav.contact')}</span>
              <FiArrowUpRight />
            </button>
          </div>

          <button
            type="button"
            className={`navbar__toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      <div className={`navbar__mobile ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="navbar__mobile-head">
          <span className="navbar__mobile-label">{t('nav.home')}</span>
        </div>
        <ul className="navbar__mobile-links">
          {navLinks.map(({ href, key }) => (
            <li key={key}>
              <a href={href} onClick={closeMenu}>{t(`nav.${key}`)}</a>
            </li>
          ))}
        </ul>
        <div className="navbar__mobile-footer">
          <button
            type="button"
            className="navbar__mobile-cta"
            onClick={() => { openContact(); closeMenu() }}
          >
            <span>{t('nav.contact')}</span>
            <FiArrowUpRight />
          </button>
          <LangDropdown mobile />
        </div>
      </div>
    </>
  )
}

export default Navbar
